import './rma-form.scss';

const ALLOWED_FILE_TYPES = [
  ".txt",".dmp",".rea",".stt",".set",".bin",".jpg",".jpeg",".bmp",".png",".mov",".mp4",".xls",".xlsx",".csv",".doc",".docx",".pdf",
];

class RMAForm {
  element: HTMLElement;
  selects: NodeListOf<HTMLSelectElement>;
  checkboxes: NodeListOf<HTMLInputElement>;
  fileUploads: FileUpload[] = [];
  
  constructor(el: HTMLElement) {
    if (!el) {
      throw new Error('RMA Error: Block Not Found');
    }

    this.element = el;
    this.selects = this.element.querySelectorAll('select');
    this.checkboxes = this.element.querySelectorAll('input[type="checkbox"]');
    
    const fileUploads = this.element.querySelectorAll('.form__file-upload');
    fileUploads.forEach(fileUpload => {
      try {
        const el = new FileUpload(fileUpload as HTMLElement);
        this.fileUploads.push(el);
      } catch(error) {
        console.error(error);
      }
    });

    this.onSelect = this.onSelect.bind(this);
    this.onCheckboxChange = this.onCheckboxChange.bind(this);

    this.initEvents();
  }

  initEvents() {
    this.selects.forEach(select => {
      select.addEventListener('change', this.onSelect);
    });

    this.checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', this.onCheckboxChange);
    })
  }

  /**
   * Toggle the visibility of related elements when selecting.
   * 
   * @param evt 
   */
  onSelect(evt: Event) {
    const select = evt.target as HTMLSelectElement;
    const container = select.closest('fieldset.rma-form__fieldsets-container');
    const subSelects = container?.querySelectorAll('[data-parent]');
    subSelects?.forEach(subSelect => {
      if ((select !== subSelect.querySelector('select')) || select.value === 'Other') {
        subSelect.classList.add('rma-form__fieldset--hidden');
      }
    });
    if (select.value) {
      container?.querySelector(`[data-parent="${select.value}"]`)?.classList.remove('rma-form__fieldset--hidden');
    }
  }

  /**
   * Toggle the visibility of a textfield when toggling its corresponding checkbox.
   * 
   * @param evt 
   * @returns 
   */
  onCheckboxChange(evt: Event) {
    const cb = evt.target as HTMLInputElement;

    if (cb.value !== 'Annet') return;

    const container = this.element.querySelector(`[data-parent="${cb.value}"]`);
    const textfield = container?.querySelector('input');
    container?.classList.toggle('form-group--hidden');
    if (cb.checked) {
      textfield?.setAttribute('required', '');
    } else {
      textfield?.removeAttribute('required');
    }
  }
}

class FileUpload {
  element: HTMLElement;
  dropzone: HTMLElement;
  input: HTMLInputElement;
  filesContainer: HTMLUListElement;
  files: FileList | null = null;
  max: number = 1;

  constructor(el: HTMLElement) {
    if (!el || !(el instanceof HTMLElement)) {
      throw new Error('FileUpload Error: Invalid container element.');
    }
    this.element = el;

    // Checks if the container element has a dropzone.
    const dropzone = this.element.querySelector('.form__file-upload-dropzone');
    if (!dropzone) {
      throw new Error('FileUpload Error: Dropzone not found.');
    }
    this.dropzone = dropzone as HTMLElement;
    this.max = parseInt(this.dropzone.dataset?.max || '0') || this.max;

    // Checks if the container element has an input of type file.
    const input = this.element.querySelector('input[type=file]');
    if (!input) {
      throw new Error('FileUpload Error: Input file element not found.');
    }
    this.input = input as HTMLInputElement;

    // Creates a container for displaying the uploaded files if possible.
    const filesContainer = this.element.querySelector('ul.form__files');
    if (filesContainer) {
      this.filesContainer = filesContainer as HTMLUListElement;
    } else {
      const ul = document.createElement('ul');
      ul.classList.add('form__files');
      this.element.appendChild(ul);
      this.filesContainer = ul;
    }

    this.initEvents();
  }

  private initEvents() {
    this.onDZClick = this.onDZClick.bind(this);
    this.onDZDragOver = this.onDZDragOver.bind(this);
    this.onDZDrop = this.onDZDrop.bind(this);
    this.showFiles = this.showFiles.bind(this);
    this.onFileOpen = this.onFileOpen.bind(this);

    this.dropzone.addEventListener('click', this.onDZClick);
    this.dropzone.addEventListener('dragover', this.onDZDragOver);
    this.dropzone.addEventListener('drop', this.onDZDrop);

    this.input.addEventListener('change', this.onFileOpen);
  }

  /**
   * Trigger the click event on the input element when clicking on the dropzone.
   */
  private onDZClick() {
    this.input.click();
  }

  /**
   * Handle dragover event.
   * 
   * @param evt 
   */
  private onDZDragOver(evt: DragEvent) {
    // This prevents the file from opening in the browser.
    evt.preventDefault();
  }

  /**
   * Handle drop event.
   * 
   * @param evt 
   * @returns 
   */
  private onDZDrop(evt: DragEvent) {
    // This prevents the file from opening in the browser.
    evt.preventDefault();

    const dropDT = evt.dataTransfer;

    if (!dropDT) return;

    this.processFiles(dropDT?.files);
  }

  /**
   * Filter uploaded files based on supported file types.
   * 
   * @param files 
   * @returns 
   */
  private validateFiles(files: FileList) {
    if (!files) return;

    const dt = new DataTransfer();

    for (const file of files) {
      const format = file.name.split('.').pop() || '';
      if (ALLOWED_FILE_TYPES.includes(`.${format}`)) {
        dt.items.add(file);
      }
    }

    return dt.files;
  }

  /**
   * Combine newly uploaded files with the existing files.
   * Then, check the file count.
   * 
   * @param newFiles 
   * @returns 
   */
  private processFiles(newFiles: FileList) {
    const validatedFiles = this.validateFiles(newFiles);

    if (!validatedFiles) return;

    if (this.files?.length === this.max) return;

    // The files (FileList) is a readonly and can't be mutated
    // so we have to create another data transfer to
    // hold all the files.
    const dt = new DataTransfer();

    if (this.files) this.addFiles(dt, this.files);
    if (validatedFiles) this.addFiles(dt, validatedFiles);

    const files = dt.files;

    if (files.length > this.max) return;

    if (validatedFiles) {
      this.showFiles(validatedFiles);
    }

    this.files = files;

    // Set the files value of the input element.
    this.input.files = this.files;
  }

  /**
   * Add the given files to the given data transfer.
   * 
   * @param dt The files will be added here.
   * @param files The files to be added to the data transfer.
   * @returns 
   */
  private addFiles(dt: DataTransfer, files: FileList) {
    if (!files) return;

    for (const file of files) {
      dt?.items.add(file);
    }
  }

  /**
   * Append and display files to the DOM.
   * 
   * @param files The files to be appended to the DOM.
   * @returns 
   */
  private showFiles(files: FileList) {
    if (!files) return;

    for (const file of files) {
      const li = document.createElement('li');
      li.innerHTML = `<span>${file.name}</span><button type="button" class="btn form__file-remove-btn btn--icon-only btn--filled"></button>`;
      this.filesContainer.appendChild(li);

      const removeBtn = li.querySelector('button');
      removeBtn?.addEventListener('click', () => {
        this.removeFile(li);
      });
    }
  }

  /**
   * Remove the file and its corresponding element from the DOM.
   * 
   * @param li The li element to be removed.
   * @returns 
   */
  private removeFile(li: HTMLLIElement) {
    if (!this.files) return;
    const index = Array.from(this.filesContainer.children).indexOf(li);

    const dt = new DataTransfer();

    for (const [idx, file] of Object.entries(this.files)) {
      if (parseInt(idx) !== index) {
        dt.items.add(file);
      }
    }

    this.files = dt.files;
    this.input.files = this.files;
    this.filesContainer.removeChild(li);
  }

  /**
   * Handle file selection.
   * 
   * @returns 
   */
  private onFileOpen() {
    if (!this.input.files) return;

    this.processFiles(this.input.files);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const blocks = document.querySelectorAll('.rma');
  blocks.forEach(block => {
    new RMAForm(block as HTMLElement);
  });
});